import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MyHttpClient } from '../http-client';
import { Observable, throwError } from 'rxjs';
import { catchError,map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  DEFAULT_SLOT_TITLE_OPTIONS,
  resolveIncentiveSlotTitleOptions,
  SlotTitleOption,
} from '../create-incentive/slot-title-options';
export const BASE_URL = {
  OLD_DASHBOARD: environment.rapido_dashboard, // make this configurable
};
@Injectable({
  providedIn: 'root'
})
export class SharedService {
  getCities = environment.server + '/api/get/cities';
  getServices = environment.server + '/api/get/services';
  getShifts = environment.server + '/api/get/shifts';
  getModes = environment.server + '/api/modes/city';
  getModesByServiceDetailIds = environment.server + '/api/modes/serviceDetailIds';
  getZones = environment.server + '/api/get/zones';
  getBulkZones = environment.server + '/api/get/bulk/zones';
  getRules = environment.server + '/api/get/rules';
  getRuleDetails = environment.server + '/api/get/rules/details/';
  incentiveMetrics = environment.server + '/api/incentive/metrics';
  getTranslations = environment.server + '/api/translations';

  STATE = {
    CREATE: 'create',
    EDIT: 'edit',
    VIEW: 'view',
    DUPLICATE: 'duplicate'
  };
  INCENTIVE_TYPES = [
    'Daily',
    'Weekly Fixed',
    'Adhoc'
  ];

  INCENTIVE_TYPES_MAP = {
    "daily": "Daily",
    "weekly": "Weekly",
    "adhoc": "Adhoc",
  }

  SEGMENT_TYPES = [
    'UHP D UHO',
    'HP D UHO',
    'HP D HO',
    'MP D UHO',
    'MP D HO',
    'MP D MO',
    'MP D LO',
    'LP D UHO',
    'LP D HO',
    'LP D MO',
    'LP D LO',
    'MP Intra UHO',
    'MP Intra HO',
    'MP Intra MO',
    'MP Intra LO',
    'LP Intra UHO',
    'LP Intra HO',
    'LP Intra MO',
    'LP Intra LO',
    'LP Inter UHO',
    'LP Inter HO',
    'LP Inter MO',
    'LP Inter LO',
    'CUSTOM'
  ];

  SUBSCRIPTION_VALIDITY_TYPES =["earnings", "rides", "unlimited"];

  fetchTranslations(locales: Array<string>, keys: Array<string>) {
    let queryparams = `?`;
    locales.forEach(locale => queryparams += `&locales[]=${locale}`);
    keys.forEach(key => queryparams += `&keys[]=${key}`);
    return this.http.get(this.getTranslations + queryparams).pipe(catchError(this.handleError));
  }

  getIncentiveMetrics(incentiveId: string) {
    return this.http.get(`${this.incentiveMetrics}?incentiveId=${incentiveId}`)
      .pipe(catchError(this.handleError));
  }

  fetchStatesAndCities() {
    return this.http.get(`${environment.server}/api/geolayers/states-and-cities`).pipe(
      catchError(this.handleError)
    );
  }

  fetchAllServiceDetails() {
    return this.http.get(`${environment.server}/api/service-details`).pipe(
      catchError(this.handleError)
    );
  }

  fetchSubscriptionServiceOptions(cityId: string, modeId: string) {
    return this.http.get(
      `${environment.server}/api/subscription/service-options?cityId=${encodeURIComponent(cityId)}&modeId=${encodeURIComponent(modeId)}`
    ).pipe(catchError(this.handleError));
  }

  fetchCities() {
return this.http.get(this.getCities).pipe(
      map((response: any) => {
        // Transform new response structure to match old structure
        const transformedResponse = {
          info: {
            status: "success",
            message: "All cities found"
          },
          data: {
            cities: (response.geolayers || []).map(geolayer => {
              return {
                active: geolayer.active,
                _id: geolayer.cityId,
                displayName: geolayer.properties && geolayer.properties.displayName ? geolayer.properties.displayName : geolayer.name,
                shortName: geolayer.properties && geolayer.properties.shortName ? geolayer.properties.shortName : geolayer.name,
                name: geolayer.properties && geolayer.properties.cityName ? geolayer.properties.cityName : geolayer.name,
              };
            }),
            count: response.count || 0
          }
        };
        return transformedResponse;
      }),
      catchError(this.handleError)
    );
  }

  fetchServices(id) {
    const payload = {
      city: id
    };
    return this.http.post(this.getServices, payload).pipe(catchError(this.handleError));
  }

  fetchServicesWithMode(cityId: string) {
    return this.http.get(`${environment.server}/api/${cityId}/services`).pipe(catchError(this.handleError));
  }

  cityService(cityId: string) {
    return this.http.get(`${environment.server}/api/serviceDetail/city/${cityId}`).pipe(catchError(this.handleError));
  }

  fetchDashboardConfig() {
    return this.http.get(`${environment.server}/api/dashboard-config`).pipe(catchError(this.handleError));
  }

  /**
   * Normalizes GET /api/dashboard-config JSON to the `data` object create-slab stores as `dashboardConfig`.
   */
  parseDashboardConfigResponse(res: any): any | null {
    if (res && res.data) {
      return res.data;
    }
    return null;
  }

  /** v2 slot title presets from dashboard-config; optional per `service._id` overrides. */
  fetchIncentiveSlotTitleOptionsFromDashboardConfig(
    serviceIds: string[] = [],
  ): Promise<SlotTitleOption[]> {
    return this.fetchDashboardConfig()
      .toPromise()
      .then((res: any) =>
        resolveIncentiveSlotTitleOptions(this.parseDashboardConfigResponse(res), serviceIds),
      )
      .catch(() => resolveIncentiveSlotTitleOptions(null, serviceIds));
  }

  isSlabCategoryConfiguredForMode(dashboardConfig: any, modeId: string, category: string): boolean {
    if (!category || !dashboardConfig || !modeId) {
      return false;
    }
    const categoryData = dashboardConfig.earningModelsForModeAndSlabCategory[modeId][category];
    return categoryData && categoryData.length > 0;
  }
  /**
   * Union of every earning model listed under `earningModelsForModeAndSlabCategory`
   * (same map create-slab reads in setEarningModels via cfg[mode][category]).
   */
  collectAllEarningModelsFromDashboardConfig(dashboardConfig: any): string[] {
    const cfg = dashboardConfig && dashboardConfig.earningModelsForModeAndSlabCategory;
    if (!cfg || typeof cfg !== 'object') {
      return [];
    }
    const unique = new Set<string>();
    Object.keys(cfg).forEach((modeId) => {
      const byCategory = cfg[modeId];
      if (!byCategory || typeof byCategory !== 'object') {
        return;
      }
      Object.keys(byCategory).forEach((category) => {
        const models = byCategory[category];
        if (Array.isArray(models)) {
          models.forEach((m) => {
            if (m != null && String(m).trim() !== '') {
              unique.add(String(m));
            }
          });
        }
      });
    });
    return Array.from(unique).sort();
  }

  getUserSelectorDetails(ruleId: any): Observable<any> {
    return this.http.get(this.getRuleDetails + ruleId).pipe(catchError(this.handleError));
  }

  fetchShifts(city) {
    return this.http.get(this.getShifts + '/' + city).pipe(catchError(this.handleError));
  }

  fetchModes(cityId: string) {
    return this.http.get(this.getModes + '/' + cityId).pipe(
      map((response: any) => {
        return {
          info: response.info || { status: "success", message: "Got modes" },
          data: {
            modes: (response.data && response.data.modes) || []
          }
        };
      }),
      catchError(this.handleError)
    );
  }

  fetchModesByServiceDetailIds(serviceDetailIds: string[]) {
    const params = new URLSearchParams();
    serviceDetailIds.forEach(id => params.append("serviceDetailIds[]", id));

    return this.http.get(`${this.getModesByServiceDetailIds}?${params.toString()}`).pipe(
      map((response: any) => {
        return {
          info: response.info || { status: "success", message: "Got modes" },
          data: {
            modes: (response.data && response.data.modes) || [],
          }
        };
      }),
      catchError((error: any) => {
        console.error('Error fetching modes by serviceDetailIds:', error);
        return throwError(error);
      })
    );
  }

  fetchRules(city, service, shift, userType, selectorType = "") {
    let queryparams = '?userType=' + userType + '&city=' + city;
    if (shift) {
      queryparams += '&shift=' + shift;
    }
    if (service) {
      queryparams += '&service=' + service;
    }

    if(selectorType) {
      queryparams += '&type=' + selectorType;
    }
    return this.http.get(this.getRules + queryparams).pipe(catchError(this.handleError));
  }

  fetchZones(city: string, groupId: string) {
    let queryparams = '?cityId=' + city + '&groupId=' + groupId;
    return this.http.get(this.getZones + queryparams).pipe(catchError(this.handleError));
  }

  fetchBulkZones(zoneIds: Array<string>) {
    const payload = {
      zoneIds
    }
    return this.http.post(this.getBulkZones, payload).pipe(catchError(this.handleError));
  }

  fetchLimits(payload: any) {
    return this.http.post(`${environment.server}/api/limit-store/limits`, payload)
      .pipe(catchError(this.handleError));
  }

  createLimit(payload: any) {
    return this.http.post(`${environment.server}/api/limit-store/limits/create`, payload)
      .pipe(catchError(this.handleError));
  }

  createShift(shiftData: { cities: string[], mode: string, shiftName: string, isDefault: boolean }) {
    const payload = {
      cities: shiftData.cities,
      modeId: shiftData.mode,
      shiftName: shiftData.shiftName,
      isDefault: shiftData.isDefault
    };
    return this.http.post(environment.server + '/api/shift', payload).pipe(catchError(this.handleError));
  }

  createSlab(slabData: any) {
    return this.http.post(environment.server + '/api/slab', slabData).pipe(catchError(this.handleError));
  }

  fetchSlabs(payload: any) {
    return this.http.post(environment.server + '/api/get/slabs', payload).pipe(catchError(this.handleError));
  }

  getSlab(id: string): Observable<any> {
    return this.http.get(`${environment.server}/api/slab/${id}`).pipe(catchError(this.handleError));
  }

  fetchGeoSlabConfig(cityId: string, layerId: string, layerType: string = 'hotspot') {
    const params = new URLSearchParams();
    params.set('cityId', cityId);
    params.set('layerId', layerId);
    params.set('layerType', layerType);
    return this.http.get(`${environment.server}/api/geo-slab-config?${params.toString()}`)
      .pipe(catchError(this.handleError));
  }

  fetchLayers(cityId: string, featureType: string = 'hotspot', groupIds: string[] = [], categories: string[] = []) {
    const params = new URLSearchParams();
    params.set('cityId', cityId);
    params.set('featureType', featureType);
    (groupIds || []).filter(Boolean).forEach((groupId) => params.append('groupId', groupId));
    (categories || []).filter(Boolean).forEach((category) => params.append('category', category));
    return this.http.get(`${environment.server}/api/layers?${params.toString()}`).pipe(
      map((response: any) => {
        const rows = (response && (response.hotspots || response.geolayers)) || [];
        return {
          layers: rows.map((row: any) => {
            const properties = row && row.properties ? row.properties : {};
            return {
              id: row.id || row._id,
              name: properties.name || properties.displayName || row.name || row.id,
              featureType: row.featureType || featureType,
              active: row.active !== false
            };
          }),
          count: response && response.count != null ? response.count : rows.length
        };
      }),
      catchError(this.handleError)
    );
  }

  fetchCaptainEarningsShifts(cityId: string, modeId: string, isDefault?: boolean) {
    const defaultParam = `&isDefault=${isDefault ? 'true' : 'false'}`;
    return this.http.get(`${environment.server}/api/captain-earnings/shifts?cityId=${cityId}&modeId=${modeId}${defaultParam}`)
      .pipe(catchError(this.handleError));
  }

  fetchMicroCorridors(payload: { cityId: string, serviceDetailIds: string[], modeId?: string }) {
    return this.http.post(`${environment.server}/api/microcorridors`, payload).pipe(catchError(this.handleError));
  }

  fetchFlatCommissionDeductions(payload: { cityId: string, serviceDetailIds: string[] }) {
    const params = new URLSearchParams();
    params.set('cityId', payload.cityId);
    payload.serviceDetailIds.forEach((id) => params.append('serviceDetailIds', id));
    return this.http.get(`${environment.server}/api/commission-deductions?${params.toString()}`)
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(error);
  }

  redirectToDashbaord(url) {
    const redirectUrl = `${BASE_URL.OLD_DASHBOARD}${url}`;
    const token = this.readCookie('token');
    window.open(`${redirectUrl}?token=${token}`);
  }

  private readCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') { c = c.substring(1, c.length); }
      if (c.indexOf(nameEQ) === 0) { return c.substring(nameEQ.length, c.length); }
    }
    return null;
  }

  constructor(private http: MyHttpClient) { }
}
